import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
	ICmmsRequestsQueryVariables,
	IRequest,
	IRequestCreateResponse,
	IRequestCreateVariables,
	IRequestDeleteSuccess,
	IRequestEditResponse,
	IRequestPayload,
	IRequestsPaginatedData,
} from '@app/pages/cmms/interfaces/request.interfaces';
import {
	GetRequestQuery,
	RequestsQuery,
	RequestsSearchQuery,
} from '@app/pages/cmms/graphql/requests.queries';
import { IAuPaginated } from '@shared/ui-components/au-paginator/pagination-page.info';
import { map, pluck, take } from 'rxjs/operators';
import {
	CreateRequest,
	DeleteRequest,
	DeleteRequests,
	EditRequest,
	UnlinkRequestObjects,
} from '@app/pages/cmms/graphql/request.mutations';
import {
	setRequestForEditing,
	storeRequestsQueryVariables,
} from '@app/pages/cmms/components/request/state/requests.action';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { Store } from '@ngrx/store';
import { IRequestState } from '@app/pages/cmms/components/request/state/requests.reducer';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { NewRequestComponent } from '@app/pages/cmms/components/request/new-request/new-request.component';
import { iconEnum } from '@app/configs/model/IconEnum';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { AuErrorAdapter } from '@app/shared';

@Injectable({
	providedIn: 'root',
})
export class RequestsService {
	constructor(
		private apollo: Apollo,
		private store$: Store<IRequestState>,
		private sidebarService: AuRightSidebarService,
		private notify: IconSnackBarService
	) {}

	loadRequests(variables: ICmmsRequestsQueryVariables) {
		// Due to filter and sort service are provided in component,
		// we need to store the query variables in store to use that in effects
		// when we update table after creating new item
		this.store$.dispatch(storeRequestsQueryVariables({ variables }));

		return this.apollo
			.query<IRequestsPaginatedData>({
				query: RequestsQuery,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(
				pluck('data', 'requests'),
				map(requests => this.getRequestsData(requests))
			);
	}

	getRequestsData(paginatedRequests: IAuPaginated<IRequest>) {
		const requests = paginatedRequests.edges.map(edge => edge.node);

		return {
			requests,
			requestPagination: {
				pageInfo: paginatedRequests.pageInfo,
				totalCount: paginatedRequests.totalCount,
			},
		};
	}

	createRequests(variables: IRequestCreateVariables) {
		return this.apollo
			.mutate<IRequestCreateResponse>({
				mutation: CreateRequest,
				variables: variables,
			})
			.pipe(pluck('data', 'createRequest'));
	}

	editRequest(
		variables: Partial<IRequestPayload>,
		unlink?: { pk: string; incidents?: string; workOrders?: string }[]
	) {
		unlink?.map(remove => {
			this.unlinkAttachedElements(remove).subscribe({
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
		});

		return this.apollo
			.mutate<IRequestEditResponse>({
				mutation: EditRequest,
				variables,
			})
			.pipe(pluck('data', 'editRequest'));
	}

	deleteRequest(pk: string) {
		return this.apollo
			.mutate<IRequestDeleteSuccess>({
				mutation: DeleteRequest,
				variables: {
					pk,
				},
			})
			.pipe(pluck('data', 'deleteIncident'));
	}

	deleteRequests(pks: string[]) {
		return this.apollo.mutate<IRequestDeleteSuccess>({
			mutation: DeleteRequests,
			fetchPolicy: 'network-only',
			variables: {
				requests: pks,
			},
		});
	}

	getRequestByPk(pk: string) {
		return this.apollo
			.query<{ request: IRequest }>({
				query: GetRequestQuery,
				fetchPolicy: 'network-only',
				variables: {
					id: pk,
				},
			})
			.pipe(pluck('data', 'request'));
	}

	unlinkAttachedElements(variables: {
		pk: string;
		workOrders?: string;
		incidents?: string;
	}) {
		return this.apollo
			.mutate<{ success: boolean }>({
				mutation: UnlinkRequestObjects,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(take(1), pluck('data'));
	}

	searchRequests(search: string) {
		return this.apollo
			.query<IRequestsPaginatedData>({
				query: RequestsSearchQuery,
				fetchPolicy: 'network-only',
				variables: { search },
			})
			.pipe(
				pluck('data', 'requests'),
				map(requests => this.getRequestsData(requests))
			);
	}

	viewRequestInWorkspace(request: IRequest) {
		this.store$.dispatch(setRequestForEditing({ requestEdit: request }));
		this.sidebarService.setComponentAndOpen(
			ACTIVE_PAGE_CONFIG[CmmsPages.editRequest].createComponent,
			{
				...ACTIVE_PAGE_CONFIG[CmmsPages.editRequest].sidebarConfig,
				title: { text: 'request_id', endfix: request.requestId },
			}
		);
	}

	openNewRequestWorkspace() {
		this.sidebarService.setComponentAndOpen(NewRequestComponent, {
			title: { text: 'new_request' },
			icon: iconEnum.request,
			actionBtnName: 'submit',
			propagateCloseEvent: true,
			showContentActions: true,
		});
	}
}
